import { EDecision, EPaymentFrequency, EPaymentPlan, EWorksheetStatus } from '@if/core/types'
import { formatCurrency, formatDate, formatDateTime } from '@if/core/utils'
import { Edit as EditIcon, History as HistoryIcon } from '@mui/icons-material'
import { Box, Button, ButtonGroup, Divider, IconButton, Link, Paper, Stack, Typography } from '@mui/material'
import { useCreateB2cWorksheet } from '@src/data/api/b2c-worksheet/b2c-worksheet-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CreditApplication, ETermMax, User } from '../../../data/types'
import { B2cWorksheet } from '../../../data/types/B2cWorksheetSchema'
import { computeTotalInsurance } from '../../../services/WorksheetComputer'
import getIFinanceMaxPaymentDateGivenActivationDate from '../viewCreditApplicationPage-selectors'

interface Props {
  creditApplication: CreditApplication
  worksheet: B2cWorksheet
  user: User | null
  editDisabled: boolean
  onWorksheetHistoryClicked: () => void
}

const B2cWorksheetInformationSection = ({
  creditApplication,
  worksheet,
  user,
  editDisabled,
  onWorksheetHistoryClicked,
}: Props) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [createWorksheetMutate, _isCreatingWorksheet] = useCreateB2cWorksheet()

  const viewWorksheetDisabled = worksheet === null
  const editWorksheetDisabled =
    creditApplication.finalCreditDecision.decision !== EDecision.Approved ||
    !user?.rights.canEditWorksheet ||
    editDisabled

  // event handlers
  const createWorksheet = useCallback(async () => {
    const route = `/Applications/b2c/${creditApplication.id}/worksheet/edit`

    if (worksheet === null) {
      const paymentAmount =
        (creditApplication.finalCreditDecision.maxAmountFinanced ?? 0) >= (creditApplication.requestedLoanAmount ?? 0)
          ? creditApplication.requestedLoanAmount
          : creditApplication.finalCreditDecision.maxAmountFinanced
      const newWorksheet = {
        amountRequested: paymentAmount,
        deliveryOn: formatDate(new Date()),
        firstPaymentOn: formatDate(getIFinanceMaxPaymentDateGivenActivationDate(new Date())),
        paymentFrequency: EPaymentFrequency.Monthly,
        term: ETermMax.twelve,
        includeInsurance: false,
        versionTag: undefined,
        paymentPlanId: EPaymentPlan.RegularDailyInterests,
        creditApplicationId: creditApplication.id,
        status: EWorksheetStatus.Draft,
      }
      try {
        await createWorksheetMutate(newWorksheet)
        navigate(route)
      } catch (error) {
        reportErrorToConsole(error)
      }
    } else {
      navigate(route)
    }
  }, [worksheet, creditApplication, createWorksheetMutate, navigate])

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
      <Box sx={{ m: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" alignItems="center" spacing={2}>
            <ButtonGroup variant="outlined" size="large">
              <Button
                id="btnEditWorksheet"
                component={Link}
                href={`/Applications/b2c/${creditApplication.id}/worksheet`}
                variant="outlined"
                disabled={viewWorksheetDisabled}
              >
                {t('worksheet.financing')}
              </Button>
              <Button onClick={createWorksheet} disabled={editWorksheetDisabled} size="small">
                <EditIcon fontSize="large" />
              </Button>
            </ButtonGroup>
          </Stack>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            alignContent="center"
            divider={<Divider orientation="vertical" flexItem sx={{ height: 25, alignSelf: 'center' }} />}
          >
            {worksheet?.updatedOn && (
              <Typography variant="body1" component="div" gutterBottom>
                {t('common.LastModification')} : {worksheet && formatDateTime(worksheet?.updatedOn)}
              </Typography>
            )}
            <IconButton
              onClick={onWorksheetHistoryClicked}
              disabled={worksheet === null}
              sx={[
                {
                  fontSize: 'large',
                },
                worksheet === null
                  ? {
                      color: '',
                    }
                  : {
                      color: 'primary.main',
                    },
              ]}
            >
              <HistoryIcon />
            </IconButton>
          </Stack>
        </Stack>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          alignItems="top"
          marginTop={1}
          justifyContent="space-evenly"
          divider={<Divider orientation="vertical" flexItem />}
        >
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.decision')} :{' '}
              {worksheet?.finalDecision.decision &&
                t(`EWorksheetDecision.${worksheet.finalDecision.decision}`, { ns: 'enum' })}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentFrequency')} :{' '}
              {worksheet && t(`EPaymentFrequency.${worksheet?.paymentFrequency}`, { ns: 'enum' })}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.term')} : {worksheet?.term} {worksheet?.term !== undefined && t('worksheet.month')}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.paymentAmount')} : {formatCurrency(worksheet?.paymentAmountForFrequency)}
            </Typography>
          </span>
          <span>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.price')} : {formatCurrency(worksheet?.amountRequested)}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.protectionPlan')} : {formatCurrency(computeTotalInsurance(worksheet))}
            </Typography>
            <Typography variant="body1" component="div" gutterBottom>
              {t('worksheet.totalAmountFinanced')} : {formatCurrency(worksheet?.totalAmountFinanced)}
            </Typography>
          </span>
        </Stack>
      </Box>
    </Paper>
  )
}

export default B2cWorksheetInformationSection
